import { ActionType, createAction } from 'typesafe-actions';
import {
    EBookType,
    IDateRange,
    IEntry,
    TStateOfScriptInjection,
} from './types';
import { ISealLog } from 'redux/app/logs/@types';

enum EInspectorConstants {
    FEATURE = '@inspector',
}

export enum EInspectorActions {
    SET_DATERANGE = '@app/inspector/setDateRange',
    CLEAR_DATERANGE = '@app/inspector/clearDateRange',

    SET_ENTRY = '@app/inspector/setEntry',

    GET_LOGS_COUNT = '@app/inspector/getLogsCount',
    SET_LOGS_COUNT = '@app/inspector/setLogsCount',

    SET_INDEX = '@app/inspector/setIndex',

    GET_LOGS = '@app/inspector/getLogs',
    GET_CHECKLISTS = '@app/inspector/getChecklists',
    SET_LOGS = '@app/inspector/setLogs',

    SET_SELECTED_LOG = '@app/inspector/setSelectedLog',

    SET_LOG_ID = '@app/inspector/setLogId',
    SET_LOG_TYPE = '@app/inspector/setLogType',

    GET_LOG_CONFIG = '@app/inspector/getLogConfig',
    GET_CHECKLIST_PRINT_TEMPLATE = '@app/inspector/getChecklistConfig',
    SET_LOG_CONFIG = '@app/inspector/setLogConfig',

    GET_LOG_PRINT_TEMPLATE = '@app/inspector/getLogPrintTemplate',
    SET_LOG_PRINT_TEMPLATE = '@app/inspector/setLogPrintTemplate',

    GET_ALL_LOGS_OF_A_DAY = '@app/inspector/getAllLogsOfADay',
    GET_DAY_LOG_PARTICULAR_DATE = '@app/inspector/getDayLogParticularDate',

    LOG_SELECT = '@app/inspector/logSelect',
    SET_ASSOCIATED_DAY_LOG = '@app/inspector/setAssociatedDayLog',

    GET_INDEX_OF_TASKS = '@app/inspector/getIndexOfTasks',
    SET_INDEX_OF_TASKS = '@app/inspector/setIndexOfTasks',

    SET_SEARCH_BY = '@app/inspector/setSearchBy',
    SET_BOOKS_USING_SEARCH_BY = '@app/inspector/setBooksUsingSearchBy',
    SET_FILTERED_BY = '@app/inspector/setFilteredBy',

    GET_APPROPRIATE_MARPOL_RECORDS = '@app/inspector/getAppropriateMarpolRecords',
    GET_SEAL_LOGS = '@app/inspector/getSealLogs',
    SET_SEAL_LOGS = '@app/inspector/setSealLogs',

    SET_SHOW_COVER_PAGES = '@app/inspector/setShowCoverPages',
    SET_IS_INJECTING_SCRIPT = '@app/inspector/setIsInjectingScript',
    PUBLIC_PRINT_VIEW_LOAD = '@app/inspector/public-print-view/load',
}

const inspectorActions = {
    commands: {
        clearDateRange: createAction(EInspectorActions.CLEAR_DATERANGE)(),
        getLogs: createAction(
			EInspectorActions.GET_LOGS,
			(filters: any, vesselId: string) => ({
				filters,
				vesselId,
			})
		)(),
        getChecklists: createAction(
			EInspectorActions.GET_CHECKLISTS,
			(filters: any) => ({
				filters
			})
		)(),
        getLogsCount: createAction(
			EInspectorActions.GET_LOGS_COUNT, 
			(vesselId: string) => ({
				vesselId
			})
		)(),
        getLogConfig: createAction(
            EInspectorActions.GET_LOG_CONFIG,
            (code: string, documentType: string, vesselId: string) => ({
                code,
                documentType,
				vesselId,
            })
        )(),
        getLogPrintTemplate: createAction(
            EInspectorActions.GET_LOG_PRINT_TEMPLATE,
            (
                code: string,
                requestingFeature: string = 'NONE',
				vesselId: string,
                recordConfigVersion?: string,
            ) => ({
                code,
                requestingFeature,
				vesselId,
                recordConfigVersion,
            })
        )(),
        getChecklistPrintTemplate: createAction(
            EInspectorActions.GET_CHECKLIST_PRINT_TEMPLATE,
            (
                code: string,
                requestingFeature: string = 'NONE',
				vesselId: string,
                recordConfigVersion?: string,
            ) => ({
                code,
                requestingFeature,
				vesselId,
                recordConfigVersion,
            })
        )(),
        getAllLogsOfADay: createAction(
            EInspectorActions.GET_ALL_LOGS_OF_A_DAY,
            (
                selectedDate: string,
                logCode: string,
                intlDateLineRecord: boolean,
				vesselId: string,
            ) => ({
                selectedDate,
                logCode,
                intlDateLineRecord,
				vesselId,
            })
        )(),
        getDayLogForAParticularDate: createAction(
            EInspectorActions.GET_DAY_LOG_PARTICULAR_DATE,
            (selectedDate: string, vesselId: string, intlDateLineRecord: boolean) => ({
                selectedDate,
				vesselId,
                intlDateLineRecord,
            })
        )(),
        getIndexOfTasks: createAction(
            EInspectorActions.GET_INDEX_OF_TASKS,
            (vesselId: string) => ({ vesselId})
        )(),
        getAppropriateMarpolRecords: createAction(
            EInspectorActions.GET_APPROPRIATE_MARPOL_RECORDS,
            // In case of marpol-views to be rendered by server, dates would be passed from urlDates, as setState in redux would not be synchronous
            (entry: IEntry, vesselId: string, urlDates?: Record<string, string>) => ({
                entry,
				vesselId,
                urlDates,
            })
        )(),
        getSealLogs: createAction(
            EInspectorActions.GET_SEAL_LOGS,
            (entry: IEntry, vesselId: string, urlDates?: Record<string, string>) => ({
                entry,
				vesselId,
                urlDates,
            })
        )(),
        publicPrintViewLoad: createAction(
            EInspectorActions.PUBLIC_PRINT_VIEW_LOAD
        )(),
    },
    document: {
        setDateRange: createAction(
            EInspectorActions.SET_DATERANGE,
            (dateRange: IDateRange) => ({
                dateRange,
            })
        )(),
        setEntry: createAction(
            EInspectorActions.SET_ENTRY,
            (entry: IEntry) => ({
                entry,
            })
        )(),

        setLogsCount: createAction(
            EInspectorActions.SET_LOGS_COUNT,
            (count: number) => ({
                count,
            })
        )(),
        setIndex: createAction(
            EInspectorActions.SET_INDEX,
            (index: number) => ({
                index,
            })
        )(),
        setLogs: createAction(EInspectorActions.SET_LOGS, (logs: any[]) => ({
            logs,
        }))(),
        setSealLogs: createAction(EInspectorActions.SET_SEAL_LOGS, (sealLogs: ISealLog[]) => ({
            sealLogs,
        }))(),
        setSelectedLog: createAction(
            EInspectorActions.SET_SELECTED_LOG,
            (selectedLog: any) => ({
                selectedLog,
            })
        )(),
        setLogId: createAction(
            EInspectorActions.SET_LOG_ID,
            (selectedLogId: string) => ({
                selectedLogId,
            })
        )(),
        setLogType: createAction(
            EInspectorActions.SET_LOG_TYPE,
            (logType: string) => ({
                logType,
            })
        )(),
        setLogConfig: createAction(
            EInspectorActions.SET_LOG_CONFIG,
            (logConfig: any) => ({
                logConfig,
            })
        )(),
        setLogPrintTemplate: createAction(
            EInspectorActions.SET_LOG_PRINT_TEMPLATE,
            (logPrintTemplate: any) => ({
                logPrintTemplate,
            })
        )(),
        logSelect: createAction(EInspectorActions.LOG_SELECT, (log: any) => ({
            log,
        }))(),
        setAssociatedDayLog: createAction(
            EInspectorActions.SET_ASSOCIATED_DAY_LOG,
            (associatedDayLog: any) => ({
                associatedDayLog,
            })
        )(),
        setIndexOfTasks: createAction(
            EInspectorActions.SET_INDEX_OF_TASKS,
            (indexOfTasks: any) => ({
                indexOfTasks,
            })
        )(),
        setSearchBy: createAction(
            EInspectorActions.SET_SEARCH_BY,
            (searchBy: string) => ({
                searchBy,
            })
        )(),
        setBooksUsingSearchBy: createAction(
            EInspectorActions.SET_BOOKS_USING_SEARCH_BY,
            (searchBy: string) => ({
                searchBy,
            })
        )(),
        setFilteredBy: createAction(
            EInspectorActions.SET_FILTERED_BY,
            (filteredBy: EBookType | null) => ({
                filteredBy,
            })
        )(),
        setShowCoverPages: createAction(
            EInspectorActions.SET_SHOW_COVER_PAGES,
            (showCoverPage: boolean) => ({
                showCoverPage,
            })
        )(),
        setStateOfScriptInjection: createAction(
            EInspectorActions.SET_IS_INJECTING_SCRIPT,
            (stateOfScriptInjection: TStateOfScriptInjection) => ({
                stateOfScriptInjection,
            })
        )(),
    },
};

export type TInspectorActions = ActionType<typeof inspectorActions>;
export { EInspectorConstants };
export default inspectorActions;
